import { createPopper, type Instance, type OptionsGeneric, type Placement, type PositioningStrategy } from '@popperjs/core'
import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { Portal } from '../Portal/Portal'

export interface PopperProps extends ComponentPropsWithoutRef<'div'> {
  /** 錨點元素，彈出層會相對此元素定位 */
  anchorEl?: HTMLElement | null
  /** 彈出層內容 */
  children?: ReactNode
  /**
   * 是否停用 Portal（不透過 Portal 渲染到 body）
   * @default false
   */
  disablePortal?: boolean
  /**
   * 是否顯示彈出層
   * @default false
   */
  open?: boolean
  /**
   * 彈出層相對於錨點的位置
   * @default 'bottom'
   */
  placement?: Placement
  /**
   * 彈出層偏移量 [skidding, distance]
   * @default [0, 8]
   */
  offset?: [number, number]
  /**
   * CSS 定位策略
   * @default 'absolute'
   */
  strategy?: PositioningStrategy
}

/**
 * Popper 是基於 @popperjs/core 的定位彈出層元件。
 * 預設透過 Portal 渲染到 body，可用 disablePortal 停用。
 */
export const Popper = (props: PopperProps) => {
  const {
    anchorEl,
    children,
    disablePortal = false,
    open = false,
    placement = 'bottom',
    offset = [0, 8],
    strategy = 'absolute',
    ...rest
  } = props

  const popperRef = useRef<HTMLDivElement>(null)
  const popperInstance = useRef<Instance | null>(null)

  useEffect(() => {
    if (!popperRef?.current || !anchorEl || !open) return
    const options: Partial<OptionsGeneric<{
      name: string
      options: {
        offset: [number, number]
      }
    }>> = {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
      ],
      strategy,
    }
    popperInstance.current = createPopper(anchorEl, popperRef.current, options)

    return () => {
      popperInstance.current?.destroy()
    }
  }, [anchorEl, offset, open, placement, strategy])

  useEffect(() => {
    if (popperInstance.current) {
      popperInstance.current.forceUpdate()
    }
  })

  if (!open) return null

  return (
    <Portal disablePortal={disablePortal}>
      <div ref={popperRef} {...rest}>{children}</div>
    </Portal>
  )
}
